/**
 * SIP Utilities - SST Console Pattern
 * 
 * Common SIP validation and parsing utilities.
 * Following SST Console patterns: utility-first, standards-compliant.
 */

import { MANDATORY_REQUEST_HEADERS, SIP_METHOD_INFO, CONDITIONAL_MANDATORY_HEADERS } from '../sip/types';

export interface SipMessage {
  method?: string | undefined;
  uri?: string | undefined;
  version: string;
  headers: Record<string, string>;
  body?: string | undefined;
  raw: string;
}

export interface SipUri {
  scheme: 'sip' | 'sips' | 'tel';
  user?: string;
  password?: string;
  host: string;
  port?: number;
  parameters?: Record<string, string>;
  displayName?: string;
}

/**
 * Validate a SIP message for basic RFC compliance
 */
export function validateSipMessage(message: string): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!message || typeof message !== 'string') {
    return { isValid: false, errors: ['Message must be a non-empty string'] };
  }
  
  const lines = message.split('\r\n');
  if (lines.length < 1) {
    return { isValid: false, errors: ['Message must have at least one line'] };
  }
  
  const firstLine = lines[0];
  if (!firstLine) {
    return { isValid: false, errors: ['First line cannot be empty'] };
  }
  
  // Check if it's a request or response
  const isRequest = /^[A-Z]+ .+ SIP\/2\.0$/.test(firstLine);
  const isResponse = /^SIP\/2\.0 \d{3} .+$/.test(firstLine);
  
  if (!isRequest && !isResponse) {
    errors.push('First line must be a valid SIP request or response');
  }
  
  // Check for mandatory headers
  const headerLines = lines.slice(1).filter(line => line && line.includes(':'));
  const headers = headerLines.map(line => {
    const parts = line.split(':', 2);
    if (parts.length < 2) return null;
    const name = parts[0];
    return name?.toLowerCase().trim() || null;
  }).filter(Boolean) as string[];
  
  const mandatoryHeaders = ['via', 'from', 'to', 'call-id', 'cseq'];
  for (const required of mandatoryHeaders) {
    if (!headers.includes(required) && !headers.includes(required.charAt(0))) { // Check short form too
      errors.push(`Missing mandatory header: ${required}`);
    }
  }
  
  // Check Max-Forwards for requests
  if (isRequest && !headers.includes('max-forwards')) {
    errors.push('Requests must include Max-Forwards header');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

export interface SipValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  isRequest: boolean;
  isResponse: boolean;
  method?: string;
  statusCode?: number;
}

const COMPACT_HEADER_MAP: Record<string, string> = {
  v: 'via',
  f: 'from',
  t: 'to',
  i: 'call-id',
  m: 'contact',
  l: 'content-length',
  c: 'content-type',
  e: 'content-encoding'
};

function unfoldHeaderLines(lines: string[]): { headerLines: string[]; body: string } {
  const headerLines: string[] = [];
  let bodyStartIndex = -1;

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (line === '') {
      bodyStartIndex = i + 1;
      break;
    }

    if (line && (line.startsWith(' ') || line.startsWith('\t'))) {
      const previous = headerLines.pop();
      if (previous !== undefined) {
        headerLines.push(`${previous} ${line.trim()}`);
      }
    } else if (line) {
      headerLines.push(line);
    }
  }

  const body = bodyStartIndex > 0 ? lines.slice(bodyStartIndex).join('\r\n') : '';
  return { headerLines, body };
}

function normalizeHeaderName(name: string): string {
  const normalized = name.trim().toLowerCase();
  if (normalized.length === 1 && COMPACT_HEADER_MAP[normalized]) {
    return COMPACT_HEADER_MAP[normalized] || normalized;
  }
  return normalized;
}

function collectHeaders(message: string): { headerNames: Set<string>; headerValues: Map<string, string[]>; body: string } {
  const lines = message.split('\r\n');
  const { headerLines, body } = unfoldHeaderLines(lines);
  const headerNames = new Set<string>();
  const headerValues = new Map<string, string[]>();

  for (const line of headerLines) {
    const colonIndex = line.indexOf(':');
    if (colonIndex <= 0) continue;
    const name = normalizeHeaderName(line.substring(0, colonIndex));
    const value = line.substring(colonIndex + 1).trim();

    headerNames.add(name);
    const existing = headerValues.get(name) || [];
    existing.push(value);
    headerValues.set(name, existing);
  }

  return { headerNames, headerValues, body };
}

function getFirstHeaderValue(headerValues: Map<string, string[]>, name: string): string | undefined {
  const values = headerValues.get(name);
  return values && values.length > 0 ? values[0] : undefined;
}

function extractMethodFromCSeq(cseqValue: string | undefined): string | undefined {
  if (!cseqValue) return undefined;
  const parts = cseqValue.trim().split(/\s+/);
  return parts.length >= 2 ? parts[1]?.toUpperCase() : undefined;
}

function extractStatusCode(startLine: string): number | undefined {
  const responseMatch = startLine.match(/^SIP\/2\.0\s+([1-6]\d{2})\s+.+$/);
  if (!responseMatch?.[1]) return undefined;
  const code = parseInt(responseMatch[1], 10);
  return Number.isNaN(code) ? undefined : code;
}

function extractRequestMethod(startLine: string): string | undefined {
  const requestMatch = startLine.match(/^([!#$%&'*+\-.^_`|~0-9A-Za-z]+)\s+.+\s+SIP\/2\.0$/);
  return requestMatch?.[1];
}

function extractViaBranch(viaValue: string): string | undefined {
  const match = viaValue.match(/branch=([^;\s]+)/i);
  return match?.[1];
}

function extractUriFromHeaderValue(value: string): string | null {
  const angleMatch = value.match(/<([^>]+)>/);
  if (angleMatch?.[1]) {
    return angleMatch[1].trim();
  }

  const trimmed = value.split(';')[0]?.trim();
  return trimmed || null;
}

function isValidCallId(value: string): boolean {
  if (!value) return false;
  return !/\s/.test(value);
}

function isValidCSeq(value: string): boolean {
  if (!value) return false;
  const parts = value.trim().split(/\s+/);
  if (parts.length < 2) return false;
  const seq = parseInt(parts[0] || '', 10);
  return !Number.isNaN(seq) && seq >= 0 && seq <= 2147483647;
}

function isValidViaHeader(value: string): boolean {
  return /^SIP\/2\.0\/(UDP|TCP|TLS|WS|WSS)\s+[^\s:;]+(?::\d+)?/.test(value);
}

export function validateSipMessageRfc(message: string): SipValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!message || typeof message !== 'string') {
    return {
      isValid: false,
      errors: ['Message must be a non-empty string'],
      warnings,
      isRequest: false,
      isResponse: false
    };
  }

  const lines = message.split('\r\n');
  const firstLine = lines[0] || '';
  const isRequest = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+\s+.+\s+SIP\/2\.0$/.test(firstLine);
  const isResponse = /^SIP\/2\.0\s+[1-6]\d{2}\s+.+$/.test(firstLine);

  if (!isRequest && !isResponse) {
    errors.push('First line must be a valid SIP request or response');
  }

  const { headerNames, headerValues, body } = collectHeaders(message);

  const requiredRequestHeaders = [...MANDATORY_REQUEST_HEADERS].map(header => header.toLowerCase());
  const requiredResponseHeaders = ['via', 'from', 'to', 'call-id', 'cseq'];

  if (isRequest) {
    for (const required of requiredRequestHeaders) {
      if (!headerNames.has(required)) {
        errors.push(`Missing mandatory header: ${required}`);
      }
    }
  }

  if (isResponse) {
    for (const required of requiredResponseHeaders) {
      if (!headerNames.has(required)) {
        errors.push(`Missing mandatory header: ${required}`);
      }
    }
  }

  const method = isRequest ? extractRequestMethod(firstLine) : undefined;
  const statusCode = isResponse ? extractStatusCode(firstLine) : undefined;

  if (isRequest && method) {
    const cseqMethod = extractMethodFromCSeq(getFirstHeaderValue(headerValues, 'cseq'));
    if (cseqMethod && cseqMethod !== method) {
      errors.push(`CSeq method (${cseqMethod}) does not match request method (${method})`);
    }

    const methodInfo = SIP_METHOD_INFO[method as keyof typeof SIP_METHOD_INFO];
    if (methodInfo?.mandatoryHeaders) {
      const mandatoryHeaders = methodInfo.mandatoryHeaders.map(header => header.toLowerCase());
      for (const header of mandatoryHeaders) {
        if (!headerNames.has(normalizeHeaderName(header))) {
          errors.push(`Missing mandatory header for ${method}: ${header}`);
        }
      }
    }
  }

  if (isResponse && statusCode !== undefined) {
    const cseqMethod = extractMethodFromCSeq(getFirstHeaderValue(headerValues, 'cseq'));
    if (cseqMethod === 'INVITE' && statusCode >= 200 && statusCode < 300) {
      const conditionalHeaders = CONDITIONAL_MANDATORY_HEADERS['2xx_INVITE'] || [];
      for (const header of conditionalHeaders) {
        if (!headerNames.has(normalizeHeaderName(header))) {
          errors.push(`Missing mandatory header for 2xx INVITE response: ${header}`);
        }
      }
    }
  }

  const viaValues = headerValues.get('via') || [];
  if (viaValues.length === 0 && (isRequest || isResponse)) {
    errors.push('Missing Via header');
  }

  for (const viaValue of viaValues) {
    const branch = extractViaBranch(viaValue);
    if (!isValidViaHeader(viaValue)) {
      errors.push('Via header has invalid format');
    }
    if (!branch) {
      errors.push('Via header missing branch parameter');
    } else if (!branch.startsWith('z9hG4bK')) {
      warnings.push('Via branch does not include z9hG4bK magic cookie');
    }
  }

  const callIdValue = getFirstHeaderValue(headerValues, 'call-id');
  if (callIdValue && !isValidCallId(callIdValue)) {
    errors.push('Call-ID contains whitespace');
  }

  const cseqValue = getFirstHeaderValue(headerValues, 'cseq');
  if (cseqValue && !isValidCSeq(cseqValue)) {
    errors.push('CSeq must be "number METHOD"');
  }

  const maxForwardsValue = getFirstHeaderValue(headerValues, 'max-forwards');
  if (isRequest) {
    if (!maxForwardsValue) {
      errors.push('Requests must include Max-Forwards header');
    } else {
      const maxForwards = parseInt(maxForwardsValue, 10);
      if (Number.isNaN(maxForwards) || maxForwards < 0 || maxForwards > 255) {
        errors.push('Max-Forwards must be an integer between 0 and 255');
      }
    }
  }

  const fromValue = getFirstHeaderValue(headerValues, 'from');
  if (fromValue) {
    const fromUri = extractUriFromHeaderValue(fromValue);
    if (!fromUri || !parseSipUri(fromUri)) {
      errors.push('From header must contain a valid SIP URI');
    }
  }

  const toValue = getFirstHeaderValue(headerValues, 'to');
  if (toValue) {
    const toUri = extractUriFromHeaderValue(toValue);
    if (!toUri || !parseSipUri(toUri)) {
      errors.push('To header must contain a valid SIP URI');
    }
  }

  const contentLengthValue = getFirstHeaderValue(headerValues, 'content-length');
  if (contentLengthValue) {
    const contentLength = parseInt(contentLengthValue, 10);
    const bodyLength = Buffer.byteLength(body || '', 'utf8');
    if (Number.isNaN(contentLength)) {
      errors.push('Content-Length must be a valid integer');
    } else if (contentLength !== bodyLength) {
      errors.push(`Content-Length mismatch (expected ${contentLength}, got ${bodyLength})`);
    }
  } else if (body && body.length > 0) {
    errors.push('Content-Length header required when body is present');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    isRequest,
    isResponse,
    ...(method !== undefined && { method }),
    ...(statusCode !== undefined && { statusCode })
  };
}

/**
 * Parse a SIP URI
 */
export function parseSipUri(uri: string): SipUri | null {
  if (!uri || typeof uri !== 'string') {
    return null;
  }
  
  // Remove display name if present
  let cleanUri = uri;
  let displayName: string | undefined;
  
  const displayMatch = uri.match(/^"?([^"]*)"?\s*<(.+)>$/);
  if (displayMatch) {
    displayName = displayMatch[1]?.trim();
    cleanUri = displayMatch[2] || '';
  }
  
  if (!cleanUri) return null;
  
  const schemeSeparator = cleanUri.indexOf(':');
  if (schemeSeparator <= 0) return null;
  const scheme = cleanUri.slice(0, schemeSeparator).toLowerCase();
  if (scheme === 'tel') return { scheme: 'tel', host: cleanUri.slice(schemeSeparator + 1) };
  if (scheme !== 'sip' && scheme !== 'sips') return null;

  const remainder = cleanUri.slice(schemeSeparator + 1);
  const atIndex = remainder.indexOf('@');
  const authorityStart = atIndex < 0 ? 0 : atIndex + 1;
  const queryIndex = remainder.indexOf('?', authorityStart);
  const authorityAndParameters = queryIndex < 0 ? remainder.slice(authorityStart) : remainder.slice(authorityStart, queryIndex);
  const parameterIndex = authorityAndParameters.indexOf(';');
  const hostPort = parameterIndex < 0 ? authorityAndParameters : authorityAndParameters.slice(0, parameterIndex);
  const parameterText = parameterIndex < 0 ? '' : authorityAndParameters.slice(parameterIndex + 1);
  const userInfo = atIndex < 0 ? undefined : remainder.slice(0, atIndex);
  const userParts = userInfo?.split(':', 2);
  const user = userParts?.[0];
  const password = userParts?.[1];
  if (userInfo !== undefined && !isValidUriComponent(userInfo, '[&=+$,;?/:A-Za-z0-9._~%!-]')) return null;

  let host: string;
  let portText: string | undefined;
  if (hostPort.startsWith('[')) {
    const close = hostPort.indexOf(']');
    if (close < 0) return null;
    host = hostPort.slice(1, close);
    const suffix = hostPort.slice(close + 1);
    if (host.length === 0 || !host.includes(':') || (suffix.length > 0 && !suffix.startsWith(':'))) return null;
    portText = suffix.length === 0 ? undefined : suffix.slice(1);
  } else {
    const colon = hostPort.indexOf(':');
    if (colon >= 0 && hostPort.indexOf(':', colon + 1) >= 0) return null;
    host = colon < 0 ? hostPort : hostPort.slice(0, colon);
    portText = colon < 0 ? undefined : hostPort.slice(colon + 1);
  }
  if (!host || /[%\s<>]/.test(host)) return null;
  const port = portText === undefined ? undefined : Number(portText);
  if (portText !== undefined && (!/^\d+$/.test(portText) || port === undefined || !Number.isInteger(port) || port < 0 || port > 65535)) return null;

  const result: SipUri = { scheme: scheme as 'sip' | 'sips', host };
  
  if (displayName) result.displayName = displayName;
  if (user) result.user = user;
  if (password) result.password = password;
  if (port !== undefined) result.port = port;
  
  // Parse parameters
  if (parameterText) {
    result.parameters = {};
    const seen = new Set<string>();
    const paramPairs = parameterText.split(';').filter(p => p);
    for (const pair of paramPairs) {
      const separator = pair.indexOf('=');
      const key = separator < 0 ? pair : pair.slice(0, separator);
      const value = separator < 0 ? '' : pair.slice(separator + 1);
      if (!key || seen.has(key.toLowerCase()) || !isValidUriComponent(key, '[\\[\\]/:&+$A-Za-z0-9._~%!-]') || !isValidUriComponent(value, '[\\[\\]/:&+$A-Za-z0-9._~%!-]')) return null;
      seen.add(key.toLowerCase());
      result.parameters[key] = value;
    }
  }

  // RFC 3261 permits repeated URI header components; this legacy Record API
  // cannot retain multiplicity, so do not reject repeated names.
  if (queryIndex >= 0) {
    for (const pair of remainder.slice(queryIndex + 1).split('&')) {
      const separator = pair.indexOf('=');
      if (separator <= 0 || !isValidUriComponent(pair.slice(0, separator), '[A-Za-z0-9._~%!-]') || !isValidUriComponent(pair.slice(separator + 1), '[\\[\\]/?:+$A-Za-z0-9._~%!-]')) return null;
    }
  }
  
  return result;
}

function isValidUriComponent(value: string, allowed: string): boolean {
  if (/%(?![0-9A-Fa-f]{2})/.test(value) || /[\u0000-\u0020\u007f<>"\\]/.test(value)) return false;
  return [...value].every((char) => char === '%' || new RegExp(allowed).test(char));
}

/**
 * Extract Call-ID from a SIP message
 */
export function extractCallId(message: string): string | null {
  if (!message || typeof message !== 'string') {
    return null;
  }
  
  const lines = message.split('\r\n');
  
  for (const line of lines) {
    if (!line) continue;
    // Check both long form and short form
    const match = line.match(/^(?:Call-ID|i):\s*(.+)$/i);
    if (match && match[1]) {
      return match[1].trim();
    }
  }
  
  return null;
}

/**
 * Parse a SIP message into components
 */
export function parseSipMessage(message: string): SipMessage | null {
  if (!message || typeof message !== 'string') {
    return null;
  }
  
  const lines = message.split('\r\n');
  if (lines.length < 1) {
    return null;
  }
  
  const firstLine = lines[0];
  if (!firstLine) return null;
  
  const headers: Record<string, string> = {};
  let bodyStartIndex = -1;
  
  // Parse headers
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    
    if (line === '') {
      bodyStartIndex = i + 1;
      break;
    }
    
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const name = line.substring(0, colonIndex).trim().toLowerCase();
      const value = line.substring(colonIndex + 1).trim();
      headers[name] = value;
    }
  }
  
  // Parse first line
  const requestMatch = firstLine.match(/^([A-Z]+)\s+(.+)\s+SIP\/2\.0$/);
  const responseMatch = firstLine.match(/^SIP\/2\.0\s+(\d{3})\s+(.+)$/);
  
  let method: string | undefined;
  let uri: string | undefined;
  let version = '2.0';
  
  if (requestMatch) {
    method = requestMatch[1];
    uri = requestMatch[2];
  } else if (responseMatch) {
    // For responses, we don't have method/uri in the same way
    method = responseMatch[1]; // Status code
    uri = responseMatch[2]; // Reason phrase
  }
  
  // Extract body
  let body: string | undefined;
  if (bodyStartIndex > 0 && bodyStartIndex < lines.length) {
    body = lines.slice(bodyStartIndex).join('\r\n');
  }
  
  return {
    method,
    uri,
    version,
    headers,
    body,
    raw: message
  };
}

/**
 * Validate IPv4 address
 */
export function validateIPv4(ip: string): boolean {
  if (!ip || typeof ip !== 'string') {
    return false;
  }
  
  const parts = ip.split('.');
  if (parts.length !== 4) {
    return false;
  }
  
  return parts.every(part => {
    const num = parseInt(part, 10);
    return !isNaN(num) && num >= 0 && num <= 255 && part === num.toString();
  });
}

/**
 * Validate port number
 * Must be an integer between 1 and 65535
 */
export function validatePort(port: number | string): boolean {
  const num = typeof port === 'string' ? parseInt(port, 10) : port;
  return !isNaN(num) && Number.isInteger(num) && num >= 1 && num <= 65535;
}

/**
 * Generate a SIP Call-ID
 */
export function generateCallId(): string {
  const timestamp = Date.now().toString(36);
  const token = randomHex(16);
  return `${timestamp}-${token}@sbclite`;
}

/**
 * Generate a SIP tag
 */
export function generateTag(): string {
  return randomHex(4);
}
import { randomHex } from './random';
